import { t as tr } from '@hana/i18n';
import './CostList.i18n';
import { useMemo, useState } from 'react';
import { IconCalendar, IconChartBar, IconDots, IconFileInvoice, IconLock, IconPlus, IconSearch, IconUpload } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Segmented, StateView, Skeleton, useToast, toggleAll, toggleOne, type ScreenState } from '@hana/ui';
import { CostJobs, jobTotals } from './CostJobs';
import { CostRequests } from './CostRequests';
import { CostAdvance } from './CostAdvance';
import { CostQuality } from './CostQuality';
import { CostMore } from './CostMore';
import { CostDock } from './CostDock';
import { MatchQueue } from './MatchQueue';
import { MatchPad } from './MatchPad';
import { JobsPad } from './JobsPad';
import { InvPad } from './InvPad';
import { AdvPad, ReqPad } from './ReqAdvPad';
import { useWide } from './useWide';
import { financeScreenState, useFinanceAccess } from './access';
import { MATCH_TONE, SOURCE_LABEL, f1, invoiceTotals, searchInvoices } from './view';
import { useT } from '@hana/i18n';
import s from './cost.module.css';
import './finance.i18n';

// Mint-Cost (desktop): tab · tìm + kỳ + Lọc · bảng hoá đơn đối tác 8 cột. Mọi dữ liệu là TIỀN → sau quyền `money`. Chỉ đọc; nút ghi báo toast.

const TABS = [
  { key: 'inv', label: 'Hoá đơn đối tác', n: 14 },
  { key: 'match', label: 'Hàng chờ ghép', n: 5 },
  { key: 'req', label: 'Đề nghị thanh toán', n: 8 },
  { key: 'job', label: 'Costing theo Job', n: 8 },
] as const;
/** Tab trong menu "Thêm ▾" (hot: warn = cam · err = đỏ theo board). */
const MORE = [
  { key: 'adv', label: 'Tạm ứng', n: 6 },
  { key: 'qa', label: 'Chất lượng chứng từ', n: 17, hot: 'err' },
] as const;
type Tab = (typeof TABS)[number]['key'] | (typeof MORE)[number]['key'];

/** Mint-Cost-iPad: 5 tab phẳng (không có menu Thêm), tên ngắn theo board. */
const PAD_TABS = [
  { key: 'match', label: 'Hàng chờ ghép', n: 5 },
  { key: 'inv', label: 'HĐ đối tác', n: 4 },
  { key: 'req', label: 'Đề nghị TT', n: 2 },
  { key: 'adv', label: 'Tạm ứng', n: 6 },
  { key: 'job', label: 'Costing', n: 8 },
] as const;

/** Rộng cột theo board (CW 1240): chọn 44 · mã HĐ 130 · đối tác 280 · nguồn 74 · tiền 110 · ghép 280 · trạng thái 136 · người 190. */
const COLS = [
  { key: 'no', header: 'Mã HĐ · ngày', w: 130 },
  { key: 'ncc', header: 'Đối tác · số HĐ · nhóm chi phí', w: 280 },
  { key: 'src', header: 'Nguồn', w: 74 },
  { key: 'amt', header: 'Tiền · VAT', w: 110, right: true },
  { key: 'match', header: 'Ghép dòng dự kiến', w: 280 },
  { key: 'st', header: 'Trạng thái', w: 136 },
  { key: 'who', header: 'Người làm · duyệt', w: 190 },
] as const;
const SEL = 62; // board: th 44 + padding-left 18 (content-box)
const TOTAL = SEL + COLS.reduce((a, c) => a + c.w, 0);
const pct = (w: number) => `${((w / TOTAL) * 100).toFixed(3)}%`;

export function CostList() {
  const toast = useToast();
  const tx = useT();
  const access = useFinanceAccess();
  const { data, error, isLoading, isError, refetch } = useList('finSupplierInvoice', { limit: 5000 });
  const vouchers = useList('finVoucher', { limit: 5000 }).data?.items ?? [];
  const wide = useWide();
  const [dockOpen, setDockOpen] = useState(true);
  const pad = useLayout() === 'pad';
  const [tab, setTab] = useState<Tab>(pad ? 'match' : 'inv');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const all = data?.items ?? [];
  const rows = useMemo(() => searchInvoices(all, q), [all, q]);
  const keys = rows.map((r) => r.id);
  const tot = invoiceTotals(rows);
  const jt = jobTotals();

  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const pickTab = (k: Tab) => setTab(k);

  let state: ScreenState = financeScreenState(access);
  if (state === 'ready') {
    if (isLoading) state = 'loading';
    else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
    else if (all.length === 0) state = 'empty';
  }
  const ready = state === 'ready';

  return (
    <Page kind="table" dock={wide && ready && dockOpen ? <CostDock invoices={all} vouchers={vouchers} onClose={() => setDockOpen(false)} onPickPartner={(p) => setQ(p)} onPickRequests={() => pickTab('req')} /> : undefined}>
      <PageHeader
        title="Chi phí"
        meta={
          <button type="button" className={s.periodChip} title="Kỳ 10/2026 đang mở · Khoá kỳ ở Quyết toán" aria-label="Kỳ 10/2026 đang mở · mở Khoá kỳ" onClick={() => toast({ msg: 'Khoá kỳ chưa mở ở bản này' })}>
            <IconLock size={13} aria-hidden="true" />
            {tr('clst.1')}
          </button>
        }
        findHint={false}
        tabs={
          <>
            <Segmented<Tab> label="Mục chi phí" value={tab} onChange={pickTab} items={(pad ? PAD_TABS : TABS).map((t) => ({ key: t.key as Tab, label: t.label, n: ready ? t.n : undefined }))} />
            {!pad && <CostMore value={tab} items={MORE} onPick={pickTab} />}
          </>
        }
        actions={
          <>
            {!wide && !pad && <HeaderTool label="Phân tích · Duyệt · Việc" icon={IconChartBar} n={8} onClick={() => toast({ msg: 'Bảng phân tích chưa mở ở bản này' })} />}
            {!pad && <HeaderTool label="Thao tác khác" icon={IconDots} onClick={noWrite} />}
            <Button variant="primary" icon={pad ? <IconUpload size={16} aria-hidden="true" /> : <IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
              {pad ? 'Import HĐ đối tác' : 'Đề nghị thanh toán'}
            </Button>
          </>
        }
      />
      <div className={`${s.page} ${s.main}`}>
        {!(pad && tab === 'match') && <div className={s.tb}>
          <label className={s.search}>
            <IconSearch size={16} aria-hidden="true" />
            <input aria-label="Tìm" placeholder={tab === 'job' ? 'Job / khách' : 'Số HĐ / đối tác / MST / job'} value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <button type="button" className={s.btn} onClick={() => toast({ msg: 'Chọn kỳ chưa mở ở bản này' })}>
            <IconCalendar size={16} aria-hidden="true" />
            {tr('clst.2')}
          </button>
          <button type="button" className={s.btn} onClick={() => toast({ msg: 'Bộ lọc chưa mở ở bản này' })}>
            <IconPlus size={16} aria-hidden="true" />
            {tr('clst.3')}
          </button>
          {ready && tab === 'inv' && !(wide && dockOpen) && (
            <div className={s.tots}>
              <span>
                {tr('clst.4')} <b>{f1(tot.total)} tr</b>
              </span>
              <span>
                {tr('clst.5')} <b className={s.warn}>{tot.unmatched} HĐ</b>
              </span>
            </div>
          )}
          {ready && tab === 'job' && (
            <div className={s.tots}>
              <span>
                {tr('clst.6')} <b className={s.warn}>+{f1(jt.varTotal)} tr</b>
              </span>
              <span>
                {tr('clst.7')} <b className={s.ok}>{f1(jt.gp)} tr</b>
              </span>
              <span>
                {tr('clst.8')} <b className={s.err}>{jt.warned} job</b>
              </span>
            </div>
          )}
        </div>}
        <StateView
          state={ready && tab === 'inv' && rows.length === 0 ? 'empty' : state}
          title={ready ? 'Không có hoá đơn khớp' : state === 'empty' ? 'Chưa có hoá đơn đối tác' : undefined}
          desc={ready ? 'Thử đổi từ khoá tìm.' : undefined}
          icon={ready || state === 'empty' ? <IconFileInvoice size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{tx('fn.retry')}</Button> : undefined}
          skeleton={<Skeleton rows={10} />}
        >
          {tab === 'job' ? (
            (pad ? <JobsPad q={q} /> : <CostJobs q={q} />)
          ) : tab === 'match' ? (
            pad ? <MatchPad /> : <MatchQueue />
          ) : pad && tab === 'inv' ? (
            <InvPad invoices={all} q={q} />
          ) : tab === 'req' ? (
            pad ? <ReqPad vouchers={vouchers} q={q} /> : <CostRequests vouchers={vouchers} q={q} />
          ) : tab === 'adv' ? (
            pad ? <AdvPad q={q} /> : <CostAdvance q={q} />
          ) : tab === 'qa' ? (
            <CostQuality />
          ) : (
          <section className={s.card} aria-label="Hoá đơn nhà cung cấp">
            <table className={`${s.table} ${selected.size ? s.picking : ''}`} aria-label="Hoá đơn đối tác" aria-rowcount={rows.length + 1}>
              <colgroup>
                <col style={{ width: pct(SEL) }} />
                {COLS.map((c) => (
                  <col key={c.key} style={{ width: pct(c.w) }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className={s.selCell}>
                    <input type="checkbox" className={s.check} aria-label="Chọn tất cả" checked={keys.length > 0 && selected.size === keys.length} onChange={() => setSelected(toggleAll(selected, keys))} />
                  </th>
                  {COLS.map((c) => (
                    <th key={c.key} scope="col" className={'right' in c ? s.right : undefined}>
                      {c.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const on = selected.has(r.id);
                  const tone = MATCH_TONE[r.state];
                  const full = r.lines > 0 && r.matched === r.lines;
                  return (
                    <tr key={r.id} className={on ? s.rowOn : undefined} aria-rowindex={i + 2} aria-selected={on} onClick={() => toast({ msg: 'Chi tiết hoá đơn chưa mở ở bản này' })}>
                      <td className={s.selCell}>
                        <input type="checkbox" className={s.check} aria-label={`Chọn ${r.id}`} checked={on} onClick={(e) => e.stopPropagation()} onChange={() => setSelected(toggleOne(selected, r.id))} />
                      </td>
                      <td>
                        <b className={s.no}>{r.id}</b>
                        <div className={s.sub}>{r.date}</div>
                      </td>
                      <td>
                        <b className={s.b} title={r.partnerName}>{r.partner}</b> <span className={s.sub}>{r.invNo}</span>
                        <div className={s.sub}>{r.group}</div>
                      </td>
                      <td>
                        <span className={s.src}>{SOURCE_LABEL[r.source]}</span>
                      </td>
                      <td className={s.right}>
                        <b className={s.b}>{f1(r.amount)}</b>
                        <div className={s.sub} title={`VAT ${f1(r.vat)}`}>
                          {f1(r.vat)}
                        </div>
                      </td>
                      <td>
                        <div className={s.match}>
                          <div className={s.bar}>
                            <i className={full ? s.full : s.part} style={{ width: r.lines ? `${Math.round((r.matched / r.lines) * 100)}%` : '0%' }} />
                          </div>
                          <span className={s.mk}>{r.lines ? `${r.matched}/${r.lines}` : '—'}</span>
                        </div>
                        <div className={s.sub} title={r.jobs}>
                          {r.jobs}
                        </div>
                      </td>
                      <td>
                        <span className={`${s.st} ${s[`st_${tone}`]}`}>
                          <span className={s.dot} aria-hidden="true" />
                          {r.state}
                        </span>
                      </td>
                      <td className={s.who}>
                        {r.maker}
                        <div className={s.sub} style={{ color: r.approver ? 'var(--ok)' : 'var(--warn)' }}>
                          {r.approver ? `Duyệt · ${r.approver}` : 'Chưa duyệt'}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className={s.foot}>{tx('fn.mTr')} · {tx('fn.nInv', { n: rows.length })}</div>
          </section>
          )}
        </StateView>
      </div>
    </Page>
  );
}
